import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faChartLine,
  faUsers,
  faCube,
  faBullhorn,
  faHandshake,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Service {
  icon: IconDefinition;
  title: string;
  description: string;
  features: string[];
}

const services: Service[] = [
  {
    icon: faRocket,
    title: 'Token Launch Marketing',
    description: 'End-to-end marketing strategy for successful token launches',
    features: ['Pre-launch hype building', 'IDO/IEO support', 'Post-launch retention'],
  },
  {
    icon: faCube,
    title: 'NFT Collection Promotion',
    description: 'Comprehensive NFT marketing to maximize mint success',
    features: ['Community building', 'Influencer partnerships', 'Marketplace optimization'],
  },
  {
    icon: faChartLine,
    title: 'DeFi Protocol Growth',
    description: 'Strategic growth hacking for DeFi platforms',
    features: ['TVL optimization', 'Yield farming campaigns', 'Liquidity incentives'],
  },
  {
    icon: faUsers,
    title: 'Community Management',
    description: 'Build and nurture engaged Web3 communities',
    features: ['24/7 moderation', 'AMA coordination', 'Community incentives'],
  },
  {
    icon: faBullhorn,
    title: 'PR & Media Outreach',
    description: 'Get featured in top crypto publications',
    features: ['Press releases', 'Media placements', 'Thought leadership'],
  },
  {
    icon: faHandshake,
    title: 'KOL & Influencer Network',
    description: 'Connect with key opinion leaders in crypto',
    features: ['Influencer matching', 'Campaign management', 'ROI tracking'],
  },
];

const ServiceCard: React.FC<{ service: Service; index: number }> = ({ service, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -10 }}
      className="glass rounded-xl p-6 hover:bg-verdigris/5 transition-all duration-300"
    >
      <motion.div
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.5 }}
        className="w-14 h-14 bg-gradient-to-br from-persian-red to-verdigris rounded-lg flex items-center justify-center mb-4"
      >
        <FontAwesomeIcon icon={service.icon} className="text-white text-xl" />
      </motion.div>

      <h3 className="text-xl font-semibold text-linen mb-3">{service.title}</h3>
      <p className="text-linen-400 mb-4">{service.description}</p>

      <ul className="space-y-2">
        {service.features.map((feature, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + i * 0.05 }}
            className="flex items-center text-sm text-linen-300"
          >
            <span className="w-1.5 h-1.5 bg-verdigris rounded-full mr-2"></span>
            {feature}
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
};

const Services: React.FC = () => {
  return (
    <section id="services" className="py-20 relative">
      <div className="max-container section-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: 'spring' }}
            className="inline-block glass px-4 py-2 rounded-full mb-4"
          >
            <span className="text-sm text-verdigris">Our Services</span>
          </motion.div>
          <h2 className="heading-2 text-linen mb-4">
            Comprehensive <span className="gradient-text">Web3 Marketing</span> Solutions
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            From token launches to NFT collections, we provide end-to-end marketing
            services tailored to your blockchain project's unique needs.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary"
          >
            Explore All Services
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;